<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '90vw', height: '85vh' }"
  header="Session Logs"
  (onHide)="close()"
>
  <div class="session-logs-container">
    <!-- Issues Summary -->
    <div class="issues-summary" *ngIf="issues.length > 0">
      <h3><i class="pi pi-exclamation-triangle"></i> Issues Detected ({{ issues.length }})</h3>
      <div class="issues-grid">
        <div *ngFor="let issue of issues" class="issue-card">
          <p-tag
            [value]="issue.issueCategory.replace('_', ' ')"
            [severity]="getSeverityClass(issue.severity)"
          ></p-tag>
          <span class="issue-count">{{ issue.count }} occurrence(s)</span>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>Source</label>
          <p-multiSelect
            [options]="sourceOptions"
            [(ngModel)]="selectedSources"
            placeholder="All Sources"
            [showClear]="true"
            (onChange)="applyFilters()"
          ></p-multiSelect>
        </div>

        <div class="filter-group">
          <label>Severity</label>
          <p-multiSelect
            [options]="severityOptions"
            [(ngModel)]="selectedSeverities"
            placeholder="All Severities"
            [showClear]="true"
            (onChange)="applyFilters()"
          ></p-multiSelect>
        </div>

        <div class="filter-group">
          <label>Event Type</label>
          <p-multiSelect
            [options]="eventTypeOptions"
            [(ngModel)]="selectedEventTypes"
            placeholder="All Events"
            [showClear]="true"
            [filter]="true"
            (onChange)="applyFilters()"
          ></p-multiSelect>
        </div>

        <div class="filter-group">
          <label>User</label>
          <p-multiSelect
            [options]="userIdentityOptions"
            [(ngModel)]="selectedUserIdentities"
            placeholder="All Users"
            [showClear]="true"
            (onChange)="applyFilters()"
          ></p-multiSelect>
        </div>

        <div class="filter-group">
          <label>Show Issues Only</label>
          <p-selectButton
            [(ngModel)]="showIssuesOnly"
            [options]="[{label: 'All', value: false}, {label: 'Issues Only', value: true}]"
            optionLabel="label"
            optionValue="value"
            (onChange)="applyFilters()"
          ></p-selectButton>
        </div>

        <div class="filter-actions">
          <p-button
            label="Clear Filters"
            icon="pi pi-filter-slash"
            [outlined]="true"
            severity="secondary"
            size="small"
            (onClick)="clearFilters()"
          ></p-button>
        </div>
      </div>
    </div>

    <!-- Logs Table -->
    <div class="logs-table-container">
      <p-table
        [value]="filteredLogs"
        [loading]="loading"
        dataKey="id"
        [scrollable]="true"
        scrollHeight="flex"
      >
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 3rem"></th>
            <th style="width: 140px">Time</th>
            <th style="width: 80px">Source</th>
            <th style="width: 200px">Event</th>
            <th style="width: 100px">User</th>
            <th style="width: 120px">Device</th>
            <th style="width: 90px">Severity</th>
            <th>Details</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-log let-expanded="expanded">
          <tr [class.issue-row]="log.isIssue">
            <td>
              <a
                href="#"
                [pRowToggler]="log"
                [pTooltip]="expanded ? 'Collapse' : 'Expand'"
                class="text-gray-600 hover:text-gray-900"
                (click)="$event.preventDefault()"
              >
                <i [class]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
              </a>
            </td>
            <td>
              <span class="timestamp">{{ formatTimestamp(log.timestamp) }}</span>
            </td>
            <td>
              <i [class]="getSourceIcon(log.source)" [pTooltip]="log.source"></i>
              {{ log.source }}
            </td>
            <td>
              <strong>{{ formatEventType(log.eventType) }}</strong>
              <i *ngIf="log.isIssue" class="pi pi-exclamation-circle issue-icon" pTooltip="Issue detected"></i>
            </td>
            <td>
              <span *ngIf="log.userIdentity">{{ log.userIdentity }}</span>
              <span *ngIf="!log.userIdentity" class="text-muted">-</span>
            </td>
            <td>
              <div class="device-info">
                <div *ngIf="log.browser">{{ log.browser }}</div>
                <small class="text-muted" *ngIf="log.os">{{ log.os }}</small>
              </div>
            </td>
            <td>
              <p-tag
                [value]="log.severity"
                [severity]="getSeverityClass(log.severity)"
              ></p-tag>
            </td>
            <td>
              <div *ngIf="log.errorMessage" class="error-message">
                {{ log.errorMessage }}
              </div>
              <div *ngIf="log.issueCategory && !log.errorMessage" class="issue-category">
                {{ log.issueCategory.replace('_', ' ') }}
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="expandedrow" let-log>
          <tr>
            <td colspan="8">
              <div class="log-details">
                <div class="details-grid">
                  <!-- Quality Metrics -->
                  <div class="detail-section" *ngIf="log.videoBitrate || log.audioBitrate || log.latency">
                    <h4>Quality Metrics</h4>
                    <div class="detail-row" *ngIf="log.videoBitrate">
                      <span class="label">Video Bitrate:</span>
                      <span class="value">{{ log.videoBitrate }} kbps</span>
                    </div>
                    <div class="detail-row" *ngIf="log.audioBitrate">
                      <span class="label">Audio Bitrate:</span>
                      <span class="value">{{ log.audioBitrate }} kbps</span>
                    </div>
                    <div class="detail-row" *ngIf="log.resolution">
                      <span class="label">Resolution:</span>
                      <span class="value">{{ log.resolution }}</span>
                    </div>
                    <div class="detail-row" *ngIf="log.framerate">
                      <span class="label">Framerate:</span>
                      <span class="value">{{ log.framerate }} fps</span>
                    </div>
                    <div class="detail-row" *ngIf="log.latency">
                      <span class="label">Latency:</span>
                      <span class="value">{{ log.latency }} ms</span>
                    </div>
                    <div class="detail-row" *ngIf="log.packetLoss !== null && log.packetLoss !== undefined">
                      <span class="label">Packet Loss:</span>
                      <span class="value">{{ log.packetLoss }}%</span>
                    </div>
                    <div class="detail-row" *ngIf="log.jitter">
                      <span class="label">Jitter:</span>
                      <span class="value">{{ log.jitter }} ms</span>
                    </div>
                  </div>

                  <!-- System Info -->
                  <div class="detail-section">
                    <h4>System Information</h4>
                    <div class="detail-row" *ngIf="log.browser">
                      <span class="label">Browser:</span>
                      <span class="value">{{ log.browser }} {{ log.browserVersion }}</span>
                    </div>
                    <div class="detail-row" *ngIf="log.os">
                      <span class="label">Operating System:</span>
                      <span class="value">{{ log.os }}</span>
                    </div>
                    <div class="detail-row" *ngIf="log.device">
                      <span class="label">Device Type:</span>
                      <span class="value">{{ log.device }}</span>
                    </div>
                    <div class="detail-row" *ngIf="log.cpuUsage !== null && log.cpuUsage !== undefined">
                      <span class="label">CPU Usage:</span>
                      <span class="value">{{ log.cpuUsage }}%</span>
                    </div>
                  </div>

                  <!-- Permissions -->
                  <div class="detail-section" *ngIf="log.cameraPermission !== null || log.microphonePermission !== null">
                    <h4>Permissions</h4>
                    <div class="detail-row" *ngIf="log.cameraPermission !== null">
                      <span class="label">Camera:</span>
                      <span class="value">
                        <i [class]="log.cameraPermission ? 'pi pi-check text-success' : 'pi pi-times text-danger'"></i>
                        {{ log.cameraPermission ? 'Granted' : 'Denied' }}
                      </span>
                    </div>
                    <div class="detail-row" *ngIf="log.microphonePermission !== null">
                      <span class="label">Microphone:</span>
                      <span class="value">
                        <i [class]="log.microphonePermission ? 'pi pi-check text-success' : 'pi pi-times text-danger'"></i>
                        {{ log.microphonePermission ? 'Granted' : 'Denied' }}
                      </span>
                    </div>
                  </div>

                  <!-- Metadata -->
                  <div class="detail-section full-width" *ngIf="log.metadata">
                    <h4>Additional Metadata</h4>
                    <pre class="metadata-json">{{ log.metadata | json }}</pre>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="8" class="text-center">
              <p *ngIf="!loading">No logs found for this session.</p>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="dialog-footer">
      <div class="footer-info">
        <span>Showing {{ filteredLogs.length }} of {{ logs.length }} logs</span>
      </div>
      <div class="footer-actions">
        <p-button
          label="Export Logs"
          icon="pi pi-download"
          [outlined]="true"
          (onClick)="exportLogs()"
          [disabled]="filteredLogs.length === 0"
        ></p-button>
        <p-button
          label="Close"
          icon="pi pi-times"
          (onClick)="close()"
        ></p-button>
      </div>
    </div>
  </ng-template>
</p-dialog>
